// @ts-strict-ignore
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { CompressionSimpleTextValueVO, ConnectionAreaUsageVO, ConnectionVO, MetaElementUsageIdentityVO, MetaElementUsageVO, TextureAreaUsageVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { DefinedMetaElementPropertyUtils } from '@article/util/defined-meta-element-property-utils';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { Dictionary } from 'typescript-collections';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { ArticleDescriptorVO, UsageArticleDescriptorVO } from '@project/model/vo/validation-context.vo';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { ProjectType } from '@project/model/project-type';
import { IsProjectType } from '@project/model/is-project-type';

export class ArticleUsageUtils {

  public static getArticleUsageInfoProperty(articleUsage: ArticleUsageVO, propertyName: string): PropertyVO {
    if (!articleUsage) {
      return null;
    }
    return PropertyUtils.getPropertyByName(articleUsage.infoProperties, propertyName);
  }

  public static getArticleUsageInfoPropertyStringValue(articleUsage: ArticleUsageVO, propertyName: string): string {
    if (!articleUsage) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(articleUsage.infoProperties, propertyName);
  }

  public static getArticleUsageInfoPropertyStringValues(articleUsage: ArticleUsageVO, propertyName: string): string[] {
    if (!articleUsage) {
      return [];
    }
    return PropertyUtils.getStringPropertyValues(articleUsage.infoProperties, propertyName);
  }

  public static setArticleUsageInfoPropertyStringValue(
    articleUsage: ArticleUsageVO,
    propertyName: string,
    value: string,
    overwriteExistingValues: boolean = true
  ): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.setStringPropertyValue(articleUsage.infoProperties, propertyName, value, overwriteExistingValues);
  }

  public static setArticleUsageInfoPropertyStringValues(
    articleUsage: ArticleUsageVO,
    propertyName: string,
    values: string[],
    overwriteExistingValues: boolean = true
  ): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.setStringPropertyValues(articleUsage.infoProperties, propertyName, values, overwriteExistingValues);
  }

  public static pushArticleUsageInfoPropertyStringValue(articleUsage: ArticleUsageVO, propertyName: string, value: string): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.pushStringPropertyValue(articleUsage.infoProperties, propertyName, value);
  }

  public static getArticleUsageInfoPropertyBooleanValue(articleUsage: ArticleUsageVO, propertyName: string): boolean {
    if (!articleUsage) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(articleUsage.infoProperties, propertyName);
  }

  public static setArticleUsageInfoPropertyBooleanValue(articleUsage: ArticleUsageVO, propertyName: string, value: boolean): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    return PropertyUtils.setBooleanPropertyValue(articleUsage.infoProperties, propertyName, value);
  }

  public static getArticleUsageInfoPropertyFloatValue(articleUsage: ArticleUsageVO, propertyName: string): number {
    if (!articleUsage) {
      return NaN;
    }
    return PropertyUtils.getFloatPropertyValue(articleUsage.infoProperties, propertyName);
  }

  public static setArticleUsageInfoPropertyFloatValue(
    articleUsage: ArticleUsageVO,
    propertyName: string,
    value: number,
    isTransientProperty: boolean = false
  ): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.setFloatPropertyValue(articleUsage.infoProperties, propertyName, value, true, isTransientProperty);
  }

  public static getArticleUsageInfoPropertyIntValue(articleUsage: ArticleUsageVO, propertyName: string): number {
    if (!articleUsage) {
      return NaN;
    }
    return PropertyUtils.getIntPropertyValue(articleUsage.infoProperties, propertyName);
  }

  public static getArticleUsageInfoPropertyIntValues(articleUsage: ArticleUsageVO, propertyName: string): number[] {
    if (!articleUsage) {
      return [];
    }
    return PropertyUtils.getIntPropertyValues(articleUsage.infoProperties, propertyName);
  }

  public static setArticleUsageInfoPropertyIntValue(articleUsage: ArticleUsageVO, propertyName: string, value: number): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.setIntPropertyValue(articleUsage.infoProperties, propertyName, value);
  }

  public static setArticleUsageInfoPropertyIntValues(
    articleUsage: ArticleUsageVO,
    propertyName: string,
    values: number[],
    overwriteExistingValues: boolean = true
  ): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.setIntPropertyValues(articleUsage.infoProperties, propertyName, values, overwriteExistingValues);
  }

  public static shiftArticleUsageInfoPropertyStringValue(articleUsage: ArticleUsageVO, propertyName: string): string {
    if (!articleUsage) {
      return null;
    }
    return PropertyUtils.shiftStringPropertyValue(articleUsage.infoProperties, propertyName);
  }

  public static shiftArticleUsageInfoPropertyIntValue(articleUsage: ArticleUsageVO, propertyName: string): number {
    if (!articleUsage) {
      return NaN;
    }
    return PropertyUtils.shiftIntPropertyValue(articleUsage.infoProperties, propertyName);
  }

  public static pushArticleUsageInfoPropertyIntValue(articleUsage: ArticleUsageVO, propertyName: string, value: number): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.pushIntPropertyValue(articleUsage.infoProperties, propertyName, value);
  }

  public static removeArticleUsageInfoProperty(articleUsage: ArticleUsageVO, propertyName: string): void {
    if (!articleUsage) {
      return;
    }
    PropertyUtils.removePropertiesByName(articleUsage.infoProperties, propertyName);
  }

  public static setArticleUsagePlausiProperty(articleUsage: ArticleUsageVO, property: PropertyVO): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.plausiProperties) {
      articleUsage.plausiProperties = [];
    }
    PropertyUtils.setProperty(articleUsage.plausiProperties, property);
  }

  public static removeArticleUsagePlausiProperty(articleUsage: ArticleUsageVO, propertyName: string): void {
    if (!articleUsage) {
      return;
    }
    PropertyUtils.removePropertiesByName(articleUsage.plausiProperties, propertyName);
  }

  public static getArticleUsagePlausiPropertyStringValue(articleUsage: ArticleUsageVO, propertyName: string): string {
    if (!articleUsage) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(articleUsage.plausiProperties, propertyName);
  }

  public static setArticleUsagePlausiPropertyStringValue(articleUsage: ArticleUsageVO, propertyName: string, value: string): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.plausiProperties) {
      articleUsage.plausiProperties = [];
    }
    PropertyUtils.setStringPropertyValue(articleUsage.plausiProperties, propertyName, value);
  }

  public static getArticleUsagePlausiPropertyBooleanValue(articleUsage: ArticleUsageVO, propertyName: string): boolean {
    if (!articleUsage) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(articleUsage.plausiProperties, propertyName);
  }

  public static setArticleUsagePlausiPropertyBooleanValue(articleUsage: ArticleUsageVO, propertyName: string, value: boolean): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.plausiProperties) {
      articleUsage.plausiProperties = [];
    }
    return PropertyUtils.setBooleanPropertyValue(articleUsage.plausiProperties, propertyName, value);
  }

  public static getArticleUsagePlausiPropertyIntValue(articleUsage: ArticleUsageVO, propertyName: string): number {
    if (!articleUsage) {
      return NaN;
    }
    return PropertyUtils.getIntPropertyValue(articleUsage.plausiProperties, propertyName);
  }

  public static setArticleUsagePlausiPropertyIntValue(articleUsage: ArticleUsageVO, propertyName: string, value: number): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.plausiProperties) {
      articleUsage.plausiProperties = [];
    }
    PropertyUtils.setIntPropertyValue(articleUsage.plausiProperties, propertyName, value);
  }

  public static getArticleUsagePlausiPropertyFloatValue(articleUsage: ArticleUsageVO, propertyName: string): number {
    if (!articleUsage) {
      return NaN;
    }
    return PropertyUtils.getFloatPropertyValue(articleUsage.plausiProperties, propertyName);
  }

  public static setArticleUsagePlausiPropertyFloatValue(articleUsage: ArticleUsageVO, propertyName: string, value: number): void {
    if (!articleUsage) {
      return;
    }
    if (!articleUsage.plausiProperties) {
      articleUsage.plausiProperties = [];
    }
    PropertyUtils.setFloatPropertyValue(articleUsage.plausiProperties, propertyName, value);
  }

  public static getDefinedMetaElementInfoPropertyStringValue(articleUsage: ArticleUsageVO, metaElementName: string, propertyName: string): string {
    if (!articleUsage) {
      return null;
    }
    const metaElementUsageIdentity = MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName);
    return DefinedMetaElementPropertyUtils.getDefinedMetaElementPropertyStringValue(
      articleUsage.definedMetaElementInfoProperties,
      metaElementUsageIdentity,
      propertyName
    );
  }

  public static removeDefinedMetaElementInfoPropertyByMetaElementUsageAndPropertyName(
    articleUsage: ArticleUsageVO,
    metaElementName: string,
    propertyName: string
  ): void {
    if (!articleUsage) {
      return;
    }
    const newMetaElementUsageIdentityVO = new MetaElementUsageIdentityVO();
    newMetaElementUsageIdentityVO.articleUsageId = articleUsage.id;
    newMetaElementUsageIdentityVO.metaElementName = metaElementName;
    DefinedMetaElementPropertyUtils.removePropertyByMetaElementUsageIdentityAndPropertyName(
      articleUsage.definedMetaElementInfoProperties,
      newMetaElementUsageIdentityVO,
      propertyName
    );
  }

  public static getAllDefinedMetaElementInfoPropertyStringValues(articleUsage: ArticleUsageVO, propertyName: string): string[] {
    if (!articleUsage) {
      return [];
    }
    return DefinedMetaElementPropertyUtils.getAllDefinedMetaElementPropertyStringValues(articleUsage.definedMetaElementInfoProperties, propertyName);
  }

  public static setDefinedMetaElementInfoPropertyStringValue(
    articleUsage: ArticleUsageVO,
    metaElementName: string,
    propertyName: string,
    value: string
  ): void {
    if (!articleUsage) {
      return;
    }
    const metaElementUsageIdentity = MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName);
    DefinedMetaElementPropertyUtils.setDefinedMetaElementPropertyStringValue(
      articleUsage.definedMetaElementInfoProperties,
      metaElementUsageIdentity,
      propertyName,
      value
    );
  }

  public static getDefinedMetaElementInfoPropertyFloatValue(articleUsage: ArticleUsageVO, metaElementName: string, propertyName: string): number {
    if (!articleUsage) {
      return null;
    }
    const metaElementUsageIdentity: MetaElementUsageIdentityVO = MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName);
    return DefinedMetaElementPropertyUtils.getDefinedMetaElementPropertyFloatValue(
      articleUsage.definedMetaElementInfoProperties,
      metaElementUsageIdentity,
      propertyName
    );
  }

  public static setDefinedMetaElementInfoPropertyFloatValue(
    articleUsage: ArticleUsageVO,
    metaElementName: string,
    propertyName: string,
    value: number
  ): void {
    if (!articleUsage) {
      return;
    }
    DefinedMetaElementPropertyUtils.setDefinedMetaElementPropertyFloatValue(
      articleUsage.definedMetaElementInfoProperties,
      MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName),
      propertyName,
      value
    );
  }

  public static setDefinedMetaElementInfoPropertyIntValue(
    articleUsage: ArticleUsageVO,
    metaElementName: string,
    propertyName: string,
    value: number
  ): void {
    if (!articleUsage) {
      return;
    }
    DefinedMetaElementPropertyUtils.setDefinedMetaElementPropertyIntValue(
      articleUsage.definedMetaElementInfoProperties,
      MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName),
      propertyName,
      value
    );
  }

  public static removeDefinedMetaElementInfoPropertiesByName(articleUsage: ArticleUsageVO, propertyName: string): void {
    if (!articleUsage) {
      return;
    }
    DefinedMetaElementPropertyUtils.removePropertiesByName(articleUsage.definedMetaElementInfoProperties, propertyName);
  }

  public static getDefinedMetaElementPlausiPropertyFloatValue(articleUsage: ArticleUsageVO, metaElementName: string, propertyName: string): number {
    if (!articleUsage) {
      return NaN;
    }
    return DefinedMetaElementPropertyUtils.getDefinedMetaElementPropertyFloatValue(
      articleUsage.definedMetaElementPlausiProperties,
      MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName),
      propertyName
    );
  }

  public static setDefinedMetaElementPlausiPropertyFloatValue(
    articleUsage: ArticleUsageVO,
    metaElementName: string,
    propertyName: string,
    value: number
  ): void {
    if (!articleUsage) {
      return;
    }
    DefinedMetaElementPropertyUtils.setDefinedMetaElementPropertyFloatValue(
      articleUsage.definedMetaElementPlausiProperties,
      MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName),
      propertyName,
      value
    );
  }

  public static getDefinedMetaElementPlausiPropertyDescription(articleUsage: ArticleUsageVO, metaElementName: string, propertyName: string): string {
    if (!articleUsage) {
      return null;
    }
    return DefinedMetaElementPropertyUtils.getDefinedMetaElementPropertyDescription(
      articleUsage.definedMetaElementPlausiProperties,
      MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName),
      propertyName
    );
  }

  public static setDefinedMetaElementPlausiPropertyDescription(
    articleUsage: ArticleUsageVO,
    metaElementName: string,
    propertyName: string,
    description: string
  ): void {
    if (!articleUsage) {
      return;
    }
    DefinedMetaElementPropertyUtils.setDefinedMetaElementPropertyDescription(
      articleUsage.definedMetaElementPlausiProperties,
      MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName),
      propertyName,
      description
    );
  }

  public static getBackwardConnectedArticleUsageIds(articleUsage: ArticleUsageVO): string[] {
    const articleUsageIds = this.getBackwardConnectedConnectionUsageIds(articleUsage).map(identity => identity.articleUsageId);
    return ArrayUtils.distinct(articleUsageIds);
  }

  public static getBackwardConnectedConnectionUsageIds(articleUsage: ArticleUsageVO): MetaElementUsageIdentityVO[] {
    if (!articleUsage || ArrayUtils.isEmpty(articleUsage.metaElementUsages)) {
      return [];
    }

    return ArrayUtils.distinctByEquality(articleUsage.metaElementUsages
      .flatMap(MetaElementUsageUtils.backwardConnectedMetaElementUsageIdentities));
  }

  public static getAcceptingAreaIds(articleUsage: ArticleUsageVO): string[] {
    if (!articleUsage) {
      return [];
    }
    const articleUsageIds = this.getForwardConnectedConnectionUsageIds(articleUsage).map(identity => identity.articleUsageId);
    return ArrayUtils.distinct(articleUsageIds);
  }

  public static getForwardConnectedConnectionUsageIds(articleUsage: ArticleUsageVO): MetaElementUsageIdentityVO[] {
    if (!articleUsage || !articleUsage.metaElementUsages || articleUsage.metaElementUsages.length === 0) {
      return [];
    }
    return articleUsage.metaElementUsages.flatMap(MetaElementUsageUtils.forwardConnectedMetaElementUsageIdentities);
  }

  public static isArticleRotated(articleUsage: ArticleUsageVO): boolean {
    let isRotated: boolean = false;
    if (articleUsage) {
      const appliedRotationAngle: number = this.getArticleUsagePlausiPropertyIntValue(
        articleUsage,
        ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
      );
      isRotated = appliedRotationAngle === 180;
    }
    return isRotated;
  }

  public static getActiveConnectionSetId(usage: ArticleUsageVO): string {
    if (!usage) {
      return null;
    }
    return this.getArticleUsageInfoPropertyStringValue(usage, ArticleUsageInfoPropertyDef.ACTIVE_CONNECTION_SET_ID);
  }

  public static getArticleIdsFromArticleUsagesDictionary(articleUsages: Dictionary<string, ArticleUsageVO>): number[] {
    if (!articleUsages) {
      return [];
    }
    const result: number[] = [];
    for (const articleUsage of articleUsages.values()) {
      const articleId: number = this.getArticleIdFromArticleUsage(articleUsage);
      if (NumberUtils.isNumber(articleId) && result.indexOf(articleId) === -1) {
        result.push(articleId);
      }
    }
    return result;
  }

  /**
   * @param articleUsage - the article usage from which the used article id should be taken from
   * @return used article id (this method also observes complex articles and returns article id of complex article.
   * So if provided article usage is part of complex article, the article id of complex article is returned.
   */
  public static getArticleIdFromArticleUsage(articleUsage: ArticleUsageVO): number {
    if (!articleUsage) {
      return NaN;
    }
    const articleId: number = this.getArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID);
    if (NumberUtils.isNumber(articleId)) {
      return articleId;
    }
    return articleUsage.articleId;
  }

  public static isSwitched(articleUsage: ArticleUsageVO): boolean {
    const isSwitched: boolean = this.getArticleUsagePlausiPropertyBooleanValue(articleUsage, ArticleUsagePlausiPropertyDef.IS_SWITCHED);
    return isSwitched;
  }

  public static updateAppliedRotationAngle(articleUsage: ArticleUsageVO, angle: number): void {
    this.updateRotationAngleProperty(articleUsage, ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE, angle);
    this.updateRotationAngleProperty(articleUsage, ArticleUsagePlausiPropertyDef.CUSTOM_APPLIED_ROTATION_ANGLE, angle);
  }

  private static updateRotationAngleProperty(articleUsage: ArticleUsageVO, propertyName: string, angle: number): void {
    if (!propertyName) {
      return;
    }
    let appliedRotationAngle: number = this.getArticleUsagePlausiPropertyIntValue(articleUsage, propertyName);
    if (NumberUtils.isNotNumber(appliedRotationAngle)) {
      appliedRotationAngle = angle;
    } else {
      appliedRotationAngle += angle;
      appliedRotationAngle %= 360;
    }
    this.setArticleUsagePlausiPropertyIntValue(articleUsage, propertyName, appliedRotationAngle);
  }

  public static toggleIsSwitchedState(articleUsage: ArticleUsageVO): void {
    const isSwitched: boolean = this.getArticleUsagePlausiPropertyBooleanValue(articleUsage, ArticleUsagePlausiPropertyDef.IS_SWITCHED);
    this.setArticleUsagePlausiPropertyBooleanValue(articleUsage, ArticleUsagePlausiPropertyDef.IS_SWITCHED, !isSwitched);
  }

  public static replaceArticleUsageId(articleUsages: ArticleUsageVO[], usageIdFrom: string, usageIdTo: string): void {
    if (!usageIdFrom || !usageIdTo || ArrayUtils.isEmpty(articleUsages) || usageIdFrom === usageIdTo) {
      return;
    }
    for (const articleUsage of articleUsages) {
      if (articleUsage.id === usageIdFrom) {
        articleUsage.id = usageIdTo;
      }
      for (const metaElementUsage of articleUsage.metaElementUsages) {
        if (metaElementUsage.identity.articleUsageId === usageIdFrom) {
          metaElementUsage.identity.articleUsageId = usageIdTo;
        }
        if (metaElementUsage instanceof ConnectionAreaUsageVO) {
          const connectionAreaUsage: ConnectionAreaUsageVO = metaElementUsage;
          if (connectionAreaUsage.connection) {
            const acceptingMetaIdentity: MetaElementUsageIdentityVO = connectionAreaUsage.connection.acceptingAreaId;
            if (acceptingMetaIdentity.articleUsageId === usageIdFrom) {
              acceptingMetaIdentity.articleUsageId = usageIdTo;
            }
            const givingAreaIds: MetaElementUsageIdentityVO[] = connectionAreaUsage.connection.givingAreaIds;
            for (const givingAreaId of givingAreaIds) {
              if (givingAreaId.articleUsageId === usageIdFrom) {
                givingAreaId.articleUsageId = usageIdTo;
              }
            }
          }
        } else if (metaElementUsage instanceof TextureAreaUsageVO) {
          const textureAreaUsage: TextureAreaUsageVO = metaElementUsage;
          const nextMergedUsageId: MetaElementUsageIdentityVO = textureAreaUsage.nextMergedUsageId;
          if (nextMergedUsageId && nextMergedUsageId.articleUsageId === usageIdFrom) {
            nextMergedUsageId.articleUsageId = usageIdTo;
          }
          const previousMergedUsageId: MetaElementUsageIdentityVO = textureAreaUsage.previousMergedUsageId;
          if (previousMergedUsageId && previousMergedUsageId.articleUsageId === usageIdFrom) {
            previousMergedUsageId.articleUsageId = usageIdTo;
          }
        }
      }
      ReferenceArticleUsageUtils.replaceReferenceArticleUsageId(articleUsage, usageIdFrom, usageIdTo);
      for (const definedMetaInfoProperty of articleUsage.definedMetaElementInfoProperties) {
        if (definedMetaInfoProperty.identity.articleUsageId === usageIdFrom) {
          definedMetaInfoProperty.identity.articleUsageId = usageIdTo;
        }
      }
    }
  }

  public static replaceArticleUsageGroupId(articleUsages: ArticleUsageVO[], groupIdFrom: string, groupIdTo: string): void {
    if (!groupIdFrom || !groupIdTo || ArrayUtils.isEmpty(articleUsages)) {
      return;
    }
    for (const usage of articleUsages) {
      if (usage.groupId === groupIdFrom) {
        usage.groupId = groupIdTo;
      }
    }
  }

  public static getEngagedAcceptingConnectionUsageIds(articleUsage: ArticleUsageVO): MetaElementUsageIdentityVO[] {
    if (!articleUsage || ArrayUtils.isEmpty(articleUsage.metaElementUsages)) {
      return [];
    }

    return this.forwardConnectionAreaUsages(articleUsage)
      .filter(ConnectionAreaUsageUtils.hasGivingAreaIdentities)
      .map(connectionAreaUsage => connectionAreaUsage.identity);
  }

  public static cloneArticleUsages(articleUsages: ArticleUsageVO[]): ArticleUsageVO[] {
    if (!articleUsages) {
      return [];
    }
    const clonedUsages: ArticleUsageVO[] = [];
    for (const usage of articleUsages) {
      clonedUsages.push(CloneUtils.cloneArticleUsageVO(usage));
    }
    return clonedUsages;
  }

  public static getArticleUsageInfoProperties(articleUsage: ArticleUsageVO, propertyNames: string[], cloneProperties: boolean): PropertyVO[] {
    if (!articleUsage || ArrayUtils.isEmpty(propertyNames)) {
      return [];
    }
    return PropertyUtils.getPropertiesByNames(articleUsage.infoProperties, propertyNames, cloneProperties);
  }

  public static replaceArticleUsageInfoProperties(articleUsage: ArticleUsageVO, properties: PropertyVO[]): void {
    if (!articleUsage || ArrayUtils.isEmpty(properties)) {
      return;
    }
    const propertyNames: string[] = [];
    properties.forEach((property: PropertyVO) => {
      if (property && property.name) {
        propertyNames.push(property.name);
      }
    });
    PropertyUtils.removePropertiesByNames(articleUsage.infoProperties, propertyNames);
    if (!articleUsage.infoProperties) {
      articleUsage.infoProperties = [];
    }
    properties.forEach((newProperty: PropertyVO) => articleUsage.infoProperties.push(newProperty));
  }

  public static getArticleUsageIdsByArticleDescriptor(articleDescriptor: ArticleDescriptorVO): string[] {
    if (articleDescriptor instanceof UsageArticleDescriptorVO) {
      const usageArticleDescriptor: UsageArticleDescriptorVO = articleDescriptor;
      return [usageArticleDescriptor.articleUsageId];
    } else {
      return [];
    }
  }

  public static getArticleUsageIdsByArticleDescriptors(articleDescriptors: ArticleDescriptorVO[]): string[] {
    if (ArrayUtils.isEmpty(articleDescriptors)) {
      return [];
    }
    const articleUsageIds: string[] = new Array<string>();
    for (const articleDescriptor of articleDescriptors) {
      articleUsageIds.push(...this.getArticleUsageIdsByArticleDescriptor(articleDescriptor));
    }
    return articleUsageIds;
  }

  public static isArticleUsageWithout3DModel(articleUsage: ArticleUsageVO): boolean {
    return this.getArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.IS_EMPTY_3D_MODEL);
  }

  public static getFirstBackwardConnectedArticleUsageId(articleUsage: ArticleUsageVO): string {
    const articleUsageIds: string[] = this.getBackwardConnectedArticleUsageIds(articleUsage);
    return articleUsageIds.length === 0 ? null : articleUsageIds[0];
  }

  public static getFirstBackwardConnectedConnectionUsageId(articleUsage: ArticleUsageVO): MetaElementUsageIdentityVO {
    const backwardConnectionIds: MetaElementUsageIdentityVO[] = this.getBackwardConnectedConnectionUsageIds(articleUsage);
    return backwardConnectionIds.length === 0 ? null : backwardConnectionIds[0];
  }

  public static isOpenEndCableArticleUsage(articleUsage: ArticleUsageVO): boolean {
    // consider that open end usage contains at least one info property with "OPEN_END_" prefix
    if (!articleUsage || !articleUsage.infoProperties) {
      return false;
    }
    for (const infoProperty of articleUsage.infoProperties) {
      const propertyName: string = infoProperty.name;
      if (!propertyName) {
        continue;
      }
      if (ArticleUsageInfoPropertyDef.OPEN_END_PREFIX === propertyName.substr(0, ArticleUsageInfoPropertyDef.OPEN_END_PREFIX.length)) {
        return true;
      }
    }
    return false;
  }

  public static getRelativeParentUsageId(articleUsage: ArticleUsageVO): string {
    return this.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.RELATIVE_PARENT_USAGE_ID);
  }

  public static isLabelingCorrectionIgnored(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.getArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.IS_LABELING_CORRECTION_IGNORED);
  }

  public static setLabelingCorrectionIgnored(articleUsage: ArticleUsageVO, value: boolean): void {
    if (!articleUsage) {
      return;
    }
    this.setArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.IS_LABELING_CORRECTION_IGNORED, value);
  }

  public static isLastTerminalInCircuit(articleUsage: ArticleUsageVO): boolean {
    return this.getArticleUsagePlausiPropertyBooleanValue(articleUsage, ArticleUsagePlausiPropertyDef.LAST_TERMINAL_IN_CIRCUIT);
  }

  public static connectionAreaUsages(articleUsage: ArticleUsageVO): ConnectionAreaUsageVO[] {
    const connectionAreaUsages: ConnectionAreaUsageVO[] = [];
    for (const metaElementUsage of articleUsage.metaElementUsages ?? []) {
      if(metaElementUsage instanceof ConnectionAreaUsageVO) {
        connectionAreaUsages.push(metaElementUsage);
      }
    }
    return connectionAreaUsages;
  }

  public static backwardConnectionAreaUsages(articleUsage: ArticleUsageVO): ConnectionAreaUsageVO[] {
    return this.connectionAreaUsages(articleUsage).filter(ConnectionAreaUsageUtils.isBackwardConnection);
  }

  public static forwardConnectionAreaUsages(articleUsage: ArticleUsageVO): ConnectionAreaUsageVO[] {
    return this.connectionAreaUsages(articleUsage).filter(ConnectionAreaUsageUtils.isForwardConnection);
  }

  public static forwardConnectedMetaElementUsageIdentities(articleUsage: ArticleUsageVO): MetaElementUsageIdentityVO[] {
    return this.connectionAreaUsages(articleUsage).flatMap(ConnectionAreaUsageUtils.forwardConnectedMetaElementUsageIdentities);
  }

  public static findMetaElementUsageByName(articleUsage: ArticleUsageVO, metaElementName: string): MetaElementUsageVO | undefined {
    if (!articleUsage || ArrayUtils.isEmpty(articleUsage.metaElementUsages) || !metaElementName) {
      return null;
    }
    return articleUsage.metaElementUsages.find(metaElementUsage => metaElementUsage.identity.metaElementName === metaElementName);
  }

  public static findConnectionAreaUsageByName(articleUsage: ArticleUsageVO, metaElementName: string): ConnectionAreaUsageVO | undefined {
    if (!articleUsage || ArrayUtils.isEmpty(articleUsage.metaElementUsages) || !metaElementName) {
      return null;
    }
    const connectionAreaUsages = this.connectionAreaUsages(articleUsage);
    return connectionAreaUsages.find(connectionAreaUsage => connectionAreaUsage.identity.metaElementName === metaElementName);
  }

  public static findTextureAreaUsageByName(articleUsage: ArticleUsageVO, metaElementName: string): TextureAreaUsageVO | undefined {
    if (!articleUsage || ArrayUtils.isEmpty(articleUsage.metaElementUsages) || !metaElementName) {
      return null;
    }
    const textureAreaUsages = this.textureAreaUsages(articleUsage);
    return textureAreaUsages.find(textureAreaUsage => textureAreaUsage.identity.metaElementName === metaElementName);
  }

  public static connectionAreaUsagesByName(articleUsage: ArticleUsageVO, metaElementName: string): ConnectionAreaUsageVO[] {
    const connectionAreaUsages = this.connectionAreaUsages(articleUsage);
    return connectionAreaUsages.filter(connectionAreaUsage => connectionAreaUsage.identity.metaElementName === metaElementName);
  }

  public static removeAssociationsWithUsages(articleUsages: ArticleUsageVO[], usageId: string): void {
    if (ArrayUtils.isEmpty(articleUsages) || !usageId) {
      return;
    }
    for (const articleUsage of articleUsages) {
      this.removeAssociationsWithUsage(articleUsage, usageId);
    }
  }

  public static removeAssociationsWithUsage(articleUsage: ArticleUsageVO, usageId: string): void {
    if (!articleUsage || !usageId) {
      return;
    }

    for(const connectionAreaUsage of this.connectionAreaUsages(articleUsage)) {
      if (usageId === connectionAreaUsage.connection.acceptingAreaId.articleUsageId) {
        articleUsage.metaElementUsages.splice(articleUsage.metaElementUsages.indexOf(connectionAreaUsage), 1);
      } else {
        const givingAreaIds: MetaElementUsageIdentityVO[] = connectionAreaUsage.connection.givingAreaIds;
        for (const givingAreaId of givingAreaIds.concat()) {
          if (givingAreaId.articleUsageId === usageId) {
            givingAreaIds.splice(givingAreaIds.indexOf(givingAreaId), 1);
          }
        }
      }
    }
    ReferenceArticleUsageUtils.removeReferenceArticleUsageId(articleUsage, usageId);
  }

  public static isCompositePartArticleUsage(articleUsage: ArticleUsageVO, useStrippedCompositeInfo: boolean = false): boolean {
    if (!articleUsage) {
      return false;
    }
    const compositeArticleUsageId: string = this.getRelatedCompositeArticleUsageId(articleUsage, useStrippedCompositeInfo);
    return !!compositeArticleUsageId;
  }

  public static getRelatedCompositeArticleUsageId(usage: ArticleUsageVO, useStrippedCompositeInfo: boolean): string {
    if (useStrippedCompositeInfo) {
      return this.getStrippedCompositeArticleUsageIdFromUsage(usage);
    } else {
      return this.getCompositeArticleUsageIdFromUsage(usage);
    }
  }

  public static getStrippedCompositeArticleUsageIdFromUsage(articleUsage: ArticleUsageVO): string {
    return this.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_USAGE_ID);
  }

  public static getCompositeArticleUsageIdFromUsage(articleUsage: ArticleUsageVO): string {
    return this.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID);
  }

  public static getCompositeOrArticleUsageId(articleUsage: ArticleUsageVO): string {
    if (!articleUsage) {
      return null;
    }
    const compositeArticleUsageId: string = this.getCompositeArticleUsageIdFromUsage(articleUsage);
    if (compositeArticleUsageId) {
      return compositeArticleUsageId;
    }
    return articleUsage.id;
  }

  public static connections(articleUsage: ArticleUsageVO): ConnectionVO[] {
    return this.connectionAreaUsages(articleUsage).map(connectionAreaUsage => connectionAreaUsage.connection);
  }

  public static forwardConnections(articleUsage: ArticleUsageVO): ConnectionVO[] {
    return this.forwardConnectionAreaUsages(articleUsage).map(connectionAreaUsage => connectionAreaUsage.connection);
  }

  public static getFirstConnectedArticleUsageIdOrEmptyByConnectionName(articleUsage: ArticleUsageVO, connectionName: string): string {
    const connectionAreaUsage = this.tryGetConnectionAreaUsageByName(articleUsage, connectionName);
    if(connectionAreaUsage && ArrayUtils.isNotEmpty(connectionAreaUsage.connection.givingAreaIds)) {
      return connectionAreaUsage.connection.givingAreaIds[0].articleUsageId;
    }
    return '';
  }

  public static getFirstConnectedArticleUsageIdOrNullByConnectionName(articleUsage: ArticleUsageVO, connectionName: string): string {
    const connectionAreaUsage = this.tryGetConnectionAreaUsageByName(articleUsage, connectionName);
    if(connectionAreaUsage && ArrayUtils.isNotEmpty(connectionAreaUsage.connection.givingAreaIds)) {
      return connectionAreaUsage.connection.givingAreaIds[0].articleUsageId;
    }
    return null;
  }

  public static isConnectionInUse(articleUsage: ArticleUsageVO, connectionName: string): boolean {
    const connectionAreaUsage = this.tryGetConnectionAreaUsageByName(articleUsage, connectionName);
    if(connectionAreaUsage && ArrayUtils.isNotEmpty(connectionAreaUsage.connection.givingAreaIds)) {
      return true;
    }
    return false;
  }

  public static tryGetConnectionAreaUsageByName(articleUsage: ArticleUsageVO, connectionName: string): ConnectionAreaUsageVO | undefined {
    for(const connectionAreaUsage of this.connectionAreaUsages(articleUsage)) {
      if(connectionAreaUsage.identity.metaElementName === connectionName) {
        return connectionAreaUsage;
      }
    }
    return undefined;
  }

  public static connectionAreaUsageByAcceptingAreaId(articleUsage: ArticleUsageVO, acceptingAreaId: MetaElementUsageIdentityVO): MetaElementUsageIdentityVO | undefined {
    for(const connectionAreaUsage of ArticleUsageUtils.connectionAreaUsages(articleUsage)) {
      if(connectionAreaUsage.connection.acceptingAreaId.equals(acceptingAreaId)) {
        return connectionAreaUsage.identity;
      }
    }
    return undefined;
  }

  public static getAcceptingAreaIdByConnectionName(articleUsage: ArticleUsageVO, connectionName: string): MetaElementUsageIdentityVO | undefined {
    for (const connectionAreaUsage of this.connectionAreaUsages(articleUsage)) {
      if(connectionAreaUsage.identity.metaElementName === connectionName) {
        return connectionAreaUsage.connection.acceptingAreaId;
      }
    }
    return undefined;
  }

  public static textureAreaUsages(articleUsage: ArticleUsageVO): TextureAreaUsageVO[] {
    return MetaElementUsageUtils.textureAreaUsages(articleUsage.metaElementUsages);
  }

  public static nonTextureAreaUsages(articleUsage: ArticleUsageVO): MetaElementUsageVO[] {
    return MetaElementUsageUtils.nonTextureAreaUsages(articleUsage.metaElementUsages);
  }

  public static tryGetFirstAsConnectionAreaUsage(articleUsage: ArticleUsageVO): ConnectionAreaUsageVO | undefined {
    if(articleUsage?.metaElementUsages[0] instanceof ConnectionAreaUsageVO) {
      return articleUsage.metaElementUsages[0];
    }
    return undefined;
  }

  public static tryGetSecondAsConnectionAreaUsage(articleUsage: ArticleUsageVO): ConnectionAreaUsageVO | undefined {
    if(articleUsage?.metaElementUsages[1] instanceof ConnectionAreaUsageVO) {
      return articleUsage.metaElementUsages[1];
    }
    return undefined;
  }

  public static containsMetaElementUsages(articleUsage: ArticleUsageVO, metaElementUsages: MetaElementUsageVO[]): boolean {
    return ArrayUtils.sourceArrayContainsAllElementsFromTargetArray(articleUsage.metaElementUsages, metaElementUsages);
  }

  public static removeMetaElementUsages(articleUsage: ArticleUsageVO, metaElementUsages: MetaElementUsageVO[]): void {
    ArrayUtils.removeAllItemsInArray(articleUsage.metaElementUsages, metaElementUsages);
  }

  public static addMetaElementUsages(articleUsage: ArticleUsageVO, metaElementUsages: MetaElementUsageVO[]): void {
    articleUsage.metaElementUsages = articleUsage.metaElementUsages.concat(metaElementUsages);
  }

  public static newConnectionAreaUsage(articleUsage: ArticleUsageVO, metaElementName: string): ConnectionAreaUsageVO {
    const connectionAreaUsage = new ConnectionAreaUsageVO();
    connectionAreaUsage.identity = MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName);
    articleUsage.metaElementUsages.push(connectionAreaUsage);
    return connectionAreaUsage;
  }

  public static newTextureAreaUsage(articleUsage: ArticleUsageVO, metaElementName: string, textArea: TextValueVO = new CompressionSimpleTextValueVO()): TextureAreaUsageVO {
    const textureAreaUsage = new TextureAreaUsageVO();
    textureAreaUsage.identity = MetaElementUsageIdentityVO.create(articleUsage.id, metaElementName);
    textureAreaUsage.textValue = textArea;
    articleUsage.metaElementUsages.push(textureAreaUsage);
    return textureAreaUsage;
  }

  public static isStrippingComplexArticleInformationRequired(projectType: ProjectType, articleUsages: ArticleUsageVO[]): boolean {
    return IsProjectType.mcsGds(projectType)
      && this.isStrippingRequiredByBaseUsages(this.findBaseArticleUsages(articleUsages));
  }

  public static isStrippingRequiredByBaseUsages(baseArticleUsages: ArticleUsageVO[]): boolean {
    // stripping should be applied only to complex articles with multiple parts
    return baseArticleUsages.length > 1;
  }

  private static findBaseArticleUsages(articleUsages: ArticleUsageVO[]): ArticleUsageVO[] {
    const result: ArticleUsageVO[] = [];
    for (const articleUsage of articleUsages) {
      const backwardConnectedArticleUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
      if (backwardConnectedArticleUsageIds.length === 0) {
        result.push(articleUsage);
      }
    }
    return result;
  }

  public static replaceCompositeArticleUsageId(articleUsages: ArticleUsageVO[], compositeUsageIdFrom: string, compositeUsageIdTo: string): void {
    if (!compositeUsageIdFrom || !compositeUsageIdTo) {
      return;
    }
    for (const articleUsage of articleUsages) {
      const originalCompositeUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(articleUsage);
      if (originalCompositeUsageId !== compositeUsageIdFrom) {
        continue;
      }
      PropertyUtils.replaceStringPropertyValue(
        articleUsage.infoProperties,
        ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID,
        compositeUsageIdFrom,
        compositeUsageIdTo
      );
    }
  }

  public static getCompositeArticleNo(articleUsage: ArticleUsageVO) {
    return this.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO);
  }

  public static getCompositeOrArticleId(articleUsage: ArticleUsageVO): number {
    const compositeId = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID);
    if (NumberUtils.isNotNumber(compositeId)) {
      return articleUsage.articleId;
    }
    return compositeId;
  }
}
